import type { ProviderDriverKind } from "@cleo/contracts";

import { NeucleosTriangle } from "../NeucleosTriangle";
import { PROVIDER_ICON_BY_PROVIDER } from "./providerIconUtils";
import { cn } from "~/lib/utils";

/**
 * PI7 (register AIU-CAN-21): the shared platform inference shows as
 * "neucleos" with the blue triangle, whatever provider serves it. The
 * principal's word 2026-09-07: "lets call the fireworks 'neucleos' (lower
 * case n) in the UI … so if we change providers that wont be exposed to
 * users" and "can we use the blue triangle as the icon, like we use icons
 * for the other providers". A label rides over the fold and never becomes
 * identity: the authority keeps the provider coordinate on the ledger rows;
 * the picker sees only the label the authority projects (the aiSdk
 * sub-provider id, or an instance display name) and brands it here.
 */
export const NEUCLEOS_BRAND_LABEL = "neucleos";

export function isNeucleosBrand(value: string | undefined): boolean {
  return value !== undefined && value.trim().toLowerCase() === NEUCLEOS_BRAND_LABEL;
}

/**
 * The provider line under a model row: "neucleos" when the sub-provider (or
 * the instance itself) is the platform lane; otherwise the instance's
 * display name with its sub-provider, as before.
 */
export function providerRowLabel(providerDisplayName: string, subProvider?: string): string {
  if (isNeucleosBrand(subProvider) || isNeucleosBrand(providerDisplayName))
    return NEUCLEOS_BRAND_LABEL;
  return subProvider ? `${providerDisplayName} · ${subProvider}` : providerDisplayName;
}

/** The glyph beside that line: the blue triangle for the platform lane, the driver's glyph otherwise. */
export function ProviderRowGlyph(props: {
  driverKind: ProviderDriverKind;
  providerDisplayName: string;
  subProvider?: string | undefined;
  className?: string;
}) {
  if (isNeucleosBrand(props.subProvider) || isNeucleosBrand(props.providerDisplayName)) {
    return <NeucleosTriangle className={cn("shrink-0 text-neucleos-identity", props.className)} />;
  }
  const Icon = PROVIDER_ICON_BY_PROVIDER[props.driverKind] ?? null;
  return Icon ? <Icon className={cn("shrink-0", props.className)} /> : null;
}
